import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Masonry from "react-masonry-css";
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { addToCart } from '../../store/cartSlice';
import { showCartToast } from '../../utils/sweetAlert';

const breakpointColumnsObj = {
  default: 4,
  1100: 3,
  768: 2,
  500: 1
};

const ProductoAICard = ({ producto, priceConfig }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const dispatch = useDispatch();

  const handleViewProduct = async (id) => {
    try {
      await fetch(`/api/productoai/${id}/view`, { method: 'POST' });
    } catch (error) {
      // Error al registrar vista
    }
  };

  const handleLikeProduct = async (id) => {
    try {
      const response = await fetch(`/api/productoai/${id}/like`, { method: 'POST' });
      const data = await response.json();
      // Aquí podrías actualizar el estado local si necesitas mostrar likes en tiempo real
    } catch (error) {
      // Error al dar like
    }
  };

  // Función para obtener el precio basado en la categoría
  // Prioridad: 1) basePrices del producto, 2) price del producto, 3) priceConfig de MongoDB
  const getProductPrice = () => {
    // 1. Si tiene basePrices en el producto (datos de MongoDB)
    if (producto.basePrices && typeof producto.basePrices === 'object') {
      const availableKeys = Object.keys(producto.basePrices);
      if (availableKeys.length > 0) {
        // Priorizar 'cuadro' si existe, sino tomar el primero
        const priceKey = producto.basePrices.cuadro ? 'cuadro' : availableKeys[0];
        return producto.basePrices[priceKey];
      }
    }
    
    // 2. Si tiene precio directo en el producto
    if (producto.price) return producto.price;
    
    // 3. Usar precios de la configuración centralizada (también de MongoDB)
    if (priceConfig?.aiProductDefaults) {
      const categoryKey = producto.category?.toLowerCase();
      return priceConfig.aiProductDefaults[categoryKey] || priceConfig.aiProductDefaults.remera || 0;
    }
    
    // 4. Sin precio disponible
    return 0;
  };

  const handleAddToCart = () => {
    const productPrice = getProductPrice();
    
    // Convertir producto AI al formato esperado por el carrito
    const cartItem = {
      _id: producto._id,
      name: producto.name,
      description: producto.description,
      price: productPrice,
      image: getDisplayImageUrl(producto.generatedImage),
      category: { name: producto.category || 'Productos AI' },
      isProductoAI: true // Flag para identificar productos AI
    };

    dispatch(addToCart(cartItem));
    showCartToast(producto.name);
  };

  return (
    <div className="product-card-ai">
      <div className="product-image-container">
        {!imageLoaded && !imageError && (
          <div className="image-placeholder">
            <i className="fa-solid fa-image"></i>
          </div>
        )}
        {imageError ? (
          <div className="image-error">
            <i className="fa-solid fa-exclamation-triangle"></i>
            <span>Error al cargar imagen</span>
          </div>
        ) : (
          <img 
            src={getDisplayImageUrl(producto.generatedImage)} 
            alt={producto.name}
            className={`product-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            onClick={() => handleViewProduct(producto._id)}
          />
        )}
        <div className="product-overlay">
          <button 
            className="btn-like"
            onClick={() => handleLikeProduct(producto._id)}
          >
            <i className="fa-solid fa-heart"></i>
            <span>{producto.likes || 0}</span>
          </button>
          <button className="btn-view">
            <i className="fa-solid fa-eye"></i>
            <span>{producto.views || 0}</span>
          </button>
        </div>
      </div>
      
      <div className="product-info">
        <h3 className="product-name">{producto.name}</h3>
        <p className="product-description">{producto.description}</p>
        
        {producto.tags && producto.tags.length > 0 && (
          <div className="product-tags">
            {producto.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
          </div>
        )}
        
        <div className="product-price">
          <span className="price">
            ${getProductPrice().toLocaleString('es-AR')}
          </span>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <button onClick={handleAddToCart} className="custom-btn">
            <i className="fa-solid fa-cart-shopping"></i>
          </button>
          <button onClick={() => {/* Ver detalles */}} className="custom-btn">
            <i className="fa-solid fa-eye"></i>
          </button>
        </div>
        
        <div className="product-meta">
          <span className="creation-date">
            <i className="fa-solid fa-calendar"></i>
            {new Date(producto.createdAt).toLocaleDateString()}
          </span>
          <span className="category">
            <i className="fa-solid fa-tag"></i>
            {producto.category}
          </span>
        </div>
      </div>
    </div>
  );
};

const ProductoAIList = () => {
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [priceConfig, setPriceConfig] = useState(null);

  const currentPage = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    fetchPriceConfig();
  }, []);

  useEffect(() => {
    fetchProductos();
  }, [clientId, currentPage, searchTerm]);

  const fetchPriceConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/price-config`);
      if (response.ok) {
        const config = await response.json();
        setPriceConfig(config);
      }
    } catch (error) {
      // Usar precios por defecto si falla
      console.log('Usando precios por defecto');
    }
  };

  const fetchProductos = async () => {
    try {
      setLoading(true);
      let url = `/api/productoai/client/${clientId}?page=${currentPage}&limit=12`;
      
      if (searchTerm) {
        url = `/api/productoai/search/${encodeURIComponent(searchTerm)}?clientId=${clientId}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Error al cargar productos');
      }
      
      const data = await response.json();
      
      if (searchTerm) {
        setProductos(data);
        setPagination({});
      } else {
        setProductos(data.productos);
        setPagination(data.pagination);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setSearchParams({ search: searchTerm.trim() });
    } else {
      setSearchParams({});
    }
  };

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page);
    setSearchParams(params);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchParams({});
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p>Cargando productos AI...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="alert alert-danger">
          <i className="fa-solid fa-exclamation-triangle"></i>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="productoai-container">
      {/* Header */}
      <div className="productoai-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="page-title">
                <i className="fa-solid fa-robot me-3"></i>
                Mis Productos AI
              </h1>
              <p className="page-subtitle">
                Productos generados con inteligencia artificial para: <strong>{clientId}</strong>
              </p>
            </div>
            <div className="col-md-4 text-md-end">
              <div className="stats">
                <div className="stat-item">
                  <span className="stat-number">{productos.length}</span>
                  <span className="stat-label">Productos</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row">
          {/* Sidebar */}
          <div className="col-lg-3 col-md-4">
            <div className="sidebar">
              <div className="search-section">
                <h4>
                  <i className="fa-solid fa-search me-2"></i>
                  Buscar Productos
                </h4>
                <form onSubmit={handleSearch} className="search-form">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar por nombre, descripción..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button type="submit" className="btn btn-success">
                      <i className="fa-solid fa-search"></i>
                    </button>
                  </div>
                </form>
                
                {searchTerm && (
                  <div className="active-search">
                    <span className="search-tag">
                      <i className="fa-solid fa-search me-1"></i>
                      "{searchTerm}"
                      <button onClick={clearSearch} className="btn-clear">
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </span>
                  </div>
                )}
              </div>

              <div className="client-info">
                <h5>
                  <i className="fa-solid fa-user me-2"></i>
                  Cliente
                </h5>
                <div className="client-card">
                  <div className="client-avatar">
                    <i className="fa-solid fa-user-circle"></i>
                  </div>
                  <div className="client-details">
                    <strong>{clientId}</strong>
                    <small>Cliente activo</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="col-lg-9 col-md-8">
            <div className="products-section">
              {/* Results Header */}
              <div className="results-header">
                <div className="results-info">
                  <h3 className="results-title">
                    {searchTerm ? (
                      <>
                        <i className="fa-solid fa-search me-2"></i>
                        Resultados para "{searchTerm}"
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-robot me-2"></i>
                        Mis Productos AI
                      </>
                    )}
                  </h3>
                  <p className="results-count">
                    {productos.length === 0 ? (
                      'No se encontraron productos'
                    ) : (
                      `${productos.length} producto${productos.length !== 1 ? 's' : ''} encontrado${productos.length !== 1 ? 's' : ''}`
                    )}
                  </p>
                </div>
              </div>

              {/* Products Grid */}
              <div className="products-grid">
                {productos.length === 0 ? (
                  <div className="no-products">
                    <div className="no-products-content">
                      <i className="fa-solid fa-robot"></i>
                      <h4>No hay productos AI para este cliente</h4>
                      <p>Este cliente aún no tiene productos generados con IA</p>
                    </div>
                  </div>
                ) : (
                  <Masonry
                    breakpointCols={breakpointColumnsObj}
                    className="masonry-grid-ai"
                    columnClassName="masonry-grid_column-ai"
                  >
                    {productos.map(producto => (
                      <div key={producto._id} className="product-item-ai">
                        <ProductoAICard producto={producto} priceConfig={priceConfig} />
                      </div>
                    ))}
                  </Masonry>
                )}
              </div>

              {/* Pagination */}
              {pagination.total > 1 && (
                <div className="pagination-container">
                  <nav>
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${pagination.current === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link"
                          onClick={() => handlePageChange(pagination.current - 1)}
                          disabled={pagination.current === 1}
                        >
                          <i className="fa-solid fa-chevron-left"></i>
                        </button>
                      </li>
                      
                      {[...Array(pagination.total)].map((_, index) => {
                        const page = index + 1;
                        return (
                          <li key={page} className={`page-item ${pagination.current === page ? 'active' : ''}`}>
                            <button 
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      })}
                      
                      <li className={`page-item ${pagination.current === pagination.total ? 'disabled' : ''}`}>
                        <button 
                          className="page-link"
                          onClick={() => handlePageChange(pagination.current + 1)}
                          disabled={pagination.current === pagination.total}
                        >
                          <i className="fa-solid fa-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductoAIList;
